// src/pages/SignupPage.js
import React, { useState, useContext } from 'react';
import API from '../api/api';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  LockOutlined,
  PersonOutline,
  ArrowForward
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '32px',
  background: '#111111',
  border: '1px solid rgba(255,255,255,0.05)',
  width: '100%',
  maxWidth: '420px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #00FF87 0%, #60EFFF 100%)',
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #00FF87 0%, #60EFFF 100%)',
  color: '#000000',
  padding: '12px 20px',
  fontSize: '15px',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '16px',
  boxShadow: '0 8px 20px rgba(0, 255, 135, 0.2)',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 28px rgba(0, 255, 135, 0.3)',
    background: 'linear-gradient(135deg, #00E077 0%, #50DFFF 100%)',
  },
  '&:disabled': {
    background: 'rgba(255,255,255,0.1)',
    boxShadow: 'none',
    color: '#666',
  }
}));

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '16px',
    backgroundColor: '#1A1A1A',
    border: '1px solid rgba(255,255,255,0.05)',
    '&:hover': {
      backgroundColor: '#1F1F1F',
      borderColor: 'rgba(255,255,255,0.1)',
    },
    '&.Mui-focused': {
      backgroundColor: '#1A1A1A',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#00FF87',
        borderWidth: '2px',
      }
    }
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.9rem',
    color: '#666',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#00FF87',
  },
  '& .MuiOutlinedInput-input': {
    color: '#FFFFFF',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.05)',
  }
});

const SignupPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      // Register the user
      const { data } = await API.post('/auth/register', {
        name: formData.name,
        email: formData.email,
        password: formData.password
      });
      
      // Auto login after successful registration
      login(data.token, data.user);
      
      // Show success message
      setSuccessMessage('Account created successfully! Redirecting to dashboard...');
      setSnackbarOpen(true);
      
      // Redirect based on role
      setTimeout(() => {
        if (data.user?.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
      }, 1500);
      
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Container 
      component="main" 
      maxWidth={false}
      disableGutters
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0A0A0A',
        backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(0, 255, 135, 0.03) 0%, transparent 50%)',
        padding: 2
      }}
    >
      <StyledPaper elevation={0}>
        {/* Logo Section - Removed tagline */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800,
              background: 'linear-gradient(135deg, #00FF87 0%, #60EFFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: '2rem'
            }}
          >
            GantryFX
          </Typography>
        </Box>

        <Typography 
          variant="h5" 
          component="h1" 
          align="center"
          sx={{ 
            fontWeight: 700, 
            color: '#FFFFFF',
            mb: 1
          }}
        >
          Create Account
        </Typography>
        <Typography 
          variant="body2" 
          align="center"
          sx={{ 
            color: '#666',
            mb: 3,
            fontSize: '0.9rem'
          }}
        >
          Get started with your free account
        </Typography>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2.5, 
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 68, 68, 0.1)',
              color: '#FF4444',
              py: 1,
              '& .MuiAlert-icon': {
                color: '#FF4444',
                fontSize: '1.2rem'
              },
              '& .MuiAlert-message': {
                fontSize: '0.85rem'
              }
            }}
          >
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <StyledTextField
              fullWidth
              name="name"
              label="Full Name"
              value={formData.name}
              onChange={handleChange}
              required
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutline sx={{ color: '#666', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              placeholder="Enter your full name"
            />

            <StyledTextField
              fullWidth
              name="email"
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ color: '#666', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              placeholder="Enter your email"
            />

            <StyledTextField
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ color: '#666', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: '#666' }}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              placeholder="Create a password (min. 6 characters)"
            />

            <StyledTextField
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ color: '#666', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              placeholder="Confirm your password"
            />

            <StyledButton
              type="submit"
              fullWidth
              disabled={loading}
              endIcon={!loading && <ArrowForward />}
              sx={{ mt: 1 }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: '#000000' }} /> : 'Create Account'}
            </StyledButton>
          </Box>
        </form>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
            Already have an account?{' '}
            <Typography
              component={Link}
              to="/"
              variant="body2"
              sx={{
                color: '#00FF87',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              Sign In
            </Typography>
          </Typography>
        </Box>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#444',
              display: 'block',
              fontSize: '0.7rem'
            }}
          >
            By signing up, you agree to our Terms of Service
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#444',
              display: 'block',
              mt: 1,
              fontSize: '0.7rem'
            }}
          >
            © {new Date().getFullYear()} GantryFX. All rights reserved.
          </Typography>
        </Box>
      </StyledPaper>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={1500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="success" 
          sx={{ 
            borderRadius: '12px',
            backgroundColor: '#00FF87',
            color: '#000000',
            fontWeight: 600,
            '& .MuiAlert-icon': {
              color: '#000000'
            }
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SignupPage;